import {getPersonMetaListAPI, PersonMetaParams} from 'module/Person/api/PersonAPI'
import {useEffect, useMemo, useState} from 'react'
import {BehaviorSubject, of, timer} from 'rxjs'
import CacheCollection from 'collection/CacheCollection'
import {catchError, debounce, map, switchMap, tap} from 'rxjs/operators'
import {log} from 'common/log'
import _ from 'lodash'
import {SlctOption} from 'module/Slct/type/SlctType'
import {Person} from 'generated/JavaTOmodifications'
import {useSelector} from 'react-redux'
import {selPersonOptions} from 'module/Person/selector/PersonSelectSelector'

export type PersonSlctOption = SlctOption<Person, number>

interface Params {
	params?: PersonMetaParams
	options?: PersonSlctOption[]
}

type RProps = Required<Params>

const cache = new CacheCollection<Promise<PersonSlctOption[]>>('PT8S')
const EMPTY: PersonSlctOption[] = []

function getCacheName(params: PersonMetaParams) {
	return [params.impl, params.startDate, params.startTime, params.endDate, params.endTime].join('|')
}

export function usePersonSlctOptions(_params?: PersonMetaParams) {
	const _options = useSelector(selPersonOptions)
	const [mapped, setMapped] = useState<PersonSlctOption[]>(EMPTY)
	const [loading, setLoading] = useState(false)
	const search$ = useMemo(() => new BehaviorSubject<Params>({params: _params, options: _options}), EMPTY)

	useEffect(() => {
		const subscription = search$.pipe(
			switchMap((x: Params) => {
				const {params, options} = x
				if (!options || !params?.impl) {
					setMapped(options || EMPTY)
					setLoading(false)
					return of(options);
				} else {
					return of(x).pipe(
						map((p: RProps) => {
							setMapped(EMPTY)
							setLoading(true)
							const cachedResult = cache.getCache(getCacheName(p.params))
							return {...p, result: cachedResult}
						}),
						debounce(({result}) => result ? of(true) : timer(600)),
						switchMap(({result, params, options}) => {
							if (result) return result
							const r = getPersonMetaListAPI(params).then(response => {
								const metaList = response.data
								if (!metaList.length || !options) return options
								const byPersNr = _.keyBy(metaList, 'persNr')
								let changedOrder = false
								const ret = options.map((o) => {
									let persNr = o.value
									if (!persNr) return o
									const meta = byPersNr[persNr]
									if (!meta) return o
									if (typeof meta.sortnr === 'number') {
										changedOrder = true
									}
									return {...o, sortnr: o.model?.sortIndex, ...meta}
								}) as PersonSlctOption[]
								if (changedOrder) {
									ret.sort((a, b) => {
										const notA = !a.sortnr && a.sortnr !== 0
										const notB = !b.sortnr && b.sortnr !== 0
										if (notA && notB) return 0
										else if (notA) return 1
										else if (notB) return -1
										// ts not working: undefined exclusion with variable value instead of full expression -> cast
										return (a.sortnr as unknown as number) - (b.sortnr as unknown as number)
									})
								}
								return ret
							})
							const cacheName = getCacheName(params)
							void cache.setCache(cacheName, r)
							return r
						}),
						tap((r) => {
							setMapped(r)
							setLoading(false)
						}),
						catchError((err) => {
							log.error('error fetching meta - using persons without meta', err);
							setMapped(x.options || EMPTY)
							setLoading(false)
							return of()
						}),
					)
				}
			}),
		).subscribe()
		return () => subscription.unsubscribe()
	}, [search$])

	useEffect(() => {
		search$.next({options: _options, params: _params})
	}, [search$, (_options || []).map(x => x.value).join('|'), Object.values(_params || {}).join("|")])
	return {loading, options: loading ? EMPTY : mapped || EMPTY}
}
